// Batch-6 cert — één afgeleide status-waarheid per (medewerker × certificaattype).
//
// `bepaalTypeStatus` is de ENIGE plek die certificaat-status berekent. Overview,
// dashboard, screening, notificatie-cron en portal gebruiken allemaal deze
// functie; er wordt nergens een verlopen/gepland-status opgeslagen (die zou
// onvermijdelijk uit de pas lopen zodra een datum passeert of een sessie
// verzet wordt). Opgeslagen wordt alleen de expliciete `lifecycle`
// (ACTIEF/VERVANGEN/VERVALLEN/NIET_VAN_TOEPASSING/GEANNULEERD).
//
// Puur: geen I/O, geen DB. Datumvergelijkingen gebeuren op de
// Europe/Amsterdam-kalenderdag (via utcToAmsterdamWallclock), nooit op de
// UTC-dag — een certificaat dat "30 juni" verloopt, verloopt op de NL-dag.

import type {
  CertificaatLifecycle,
  CertificaatTypeStatus,
  CertificaatVerificatieStatus,
} from '@streep/types'
import { isVoorlopigeSessieStatus } from '@streep/types'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'

// ─── Statussen ────────────────────────────────────────────────────────────────
//
// De status-union + labels leven in @streep/types (DTO-laag, hrm.ts); de
// resolver hier is de enige plek die ze berekent. Type-alias her-exporteren
// zodat resolver-gebruikers niet twee packages hoeven te importeren.

export type { CertificaatTypeStatus } from '@streep/types'

// ─── Invoer ───────────────────────────────────────────────────────────────────

/** Lifecycle-waarden zoals op EmployeeCertificate (string-union van @streep/types). */
export type CertificaatLifecycleKey = CertificaatLifecycle

export interface CertificaatVersieInput {
  lifecycle: CertificaatLifecycleKey
  versie: number
  /** UTC-instant of ISO-string; null = verloopt nooit. */
  expiresAt: Date | string | null
  /**
   * batch-54 certificaten (FO-408 §A3) — verificatietoestand van het bewijsstuk.
   * DE screening-guard van de hele applicatie zit hier, in deze ene resolver:
   * alleen BEVESTIGD telt als geldig certificaat. Weglaten = BEVESTIGD (de
   * DB-default), zodat callers die het veld niet selecteren ongewijzigd blijven
   * werken.
   */
  verificatieStatus?: CertificaatVerificatieStatus
}

/**
 * Een geplande/lopende OpleidingDeelname van een opleiding die (via
 * OpleidingCertificaatKoppeling) dit certificaattype oplevert. De caller
 * filtert zelf op de koppeling; de resolver filtert op status + datum.
 */
export interface CertificaatDeelnameInput {
  /** DeelnameStatus (string-union; alleen AANGEMELD/INGEPLAND/BEZIG tellen). */
  status: string
  /** SessieStatus; GEANNULEERD telt niet mee. Null bij ontbrekende sessie. */
  sessieStatus: string | null
  /** Eerste cursusdag (sessie.datum). Null = nog geen datum → telt NIET als GEPLAND. */
  sessieDatum: Date | string | null
  /** Laatste cursusdag (sessie.eindDatum, fallback sessie.datum). */
  sessieEindDatum: Date | string | null
  /**
   * batch-63 integratie (FO-468 x FO-469) - status van de cursusaanvraag bij de
   * opleider achter een VOORLOPIGE sessie (SessieStatus AANGEVRAAGD), of null.
   * Weglaten/null = geen aanvraag bekend: de voorlopige sessie telt dan als open.
   */
  cursusAanvraagStatus?: string | null
}

export interface BepaalTypeStatusOpts {
  /** Het "nu"-instant; intern herleid naar de Europe/Amsterdam-kalenderdag. */
  vandaag: Date
  /**
   * Waarschuwingstermijnen in dagen (instelbaar, default [90, 60, 30, 0]).
   * De resolver gebruikt het maximum als VERLOOPT_BINNENKORT-venster; de
   * losse waarden zijn voor de notificatie-cron (melden op exact die dagen).
   */
  waarschuwingstermijnenDagen: number[]
}

export interface TypeStatusResultaat<
  V extends CertificaatVersieInput,
  D extends CertificaatDeelnameInput,
> {
  status: CertificaatTypeStatus
  /**
   * De versie die de status draagt: de actieve geldige versie (ACTIEF /
   * VERLOOPT_BINNENKORT), anders de jongste niet-geannuleerde versie
   * (VERLOPEN / NIET_VAN_TOEPASSING / pipeline-statussen met historie).
   * Null wanneer er nooit een versie was.
   */
  leidendeVersie: V | null
  /**
   * De deelname die GEPLAND/IN_OPLEIDING/WACHT_OP_CERTIFICAAT bepaalt:
   * de lopende, anders de eerstkomende toekomstige, anders de meest recent
   * geëindigde. Null buiten die statussen.
   */
  relevanteDeelname: D | null
  /**
   * Kalenderdagen (NL) tot de vervaldatum van de actieve geldige versie.
   * 0 = verloopt vandaag. Null wanneer er geen geldige versie is of die
   * nooit verloopt.
   */
  dagenTotVerloop: number | null
}

// ─── NL-dag-helpers ───────────────────────────────────────────────────────────

/** Europe/Amsterdam-kalenderdag ('yyyy-mm-dd') van een UTC-instant. */
export function nlDagVanInstant(d: Date | string): string {
  return utcToAmsterdamWallclock(typeof d === 'string' ? new Date(d) : d).ymd
}

/** Hele kalenderdagen tussen twee 'yyyy-mm-dd'-strings (tot − van). */
export function dagenTussenNlDagen(vanYmd: string, totYmd: string): number {
  const parse = (ymd: string): number => {
    const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10))
    return Date.UTC(y, m - 1, d)
  }
  return Math.round((parse(totYmd) - parse(vanYmd)) / 86_400_000)
}

// ─── Resolver ─────────────────────────────────────────────────────────────────

const PIPELINE_DEELNAME_STATUSSEN = new Set(['AANGEMELD', 'INGEPLAND', 'BEZIG'])

/**
 * batch-63 integratie (FO-468) - een cursusaanvraag met deze status draagt de
 * voorlopige sessie niet meer: de opleider wees af (de planner moet aanpassen of
 * annuleren) of de aanvraag is ingetrokken. Zo'n deelname is geen planning.
 */
const VERVALLEN_CURSUSAANVRAAG_STATUSSEN = new Set(['AFGEWEZEN', 'GEANNULEERD'])

function isVoorlopig(d: CertificaatDeelnameInput): boolean {
  return isVoorlopigeSessieStatus(d.sessieStatus)
}

/**
 * Draagt een deelname op een voorlopige sessie (nog) een planning? De
 * cursusaanvraag plant de deelnemers INGEPLAND; een afwijzing geeft de dag vrij en
 * zet ze terug op AANGEMELD, ook als er daarna een nieuw antwoord ter beoordeling
 * komt. Een ingetrokken of afgewezen aanvraag telt nooit.
 */
function voorlopigGepland(d: CertificaatDeelnameInput): boolean {
  if (d.status === 'AANGEMELD') return false
  return !VERVALLEN_CURSUSAANVRAAG_STATUSSEN.has(d.cursusAanvraagStatus ?? '')
}

/** Alleen door HR bevestigde versies tellen mee als certificaat (FO-408 §A3). */
function isBevestigd(v: CertificaatVersieInput): boolean {
  return (v.verificatieStatus ?? 'BEVESTIGD') === 'BEVESTIGD'
}

/**
 * Bepaal de afgeleide status van één certificaattype voor één medewerker.
 *
 *   1. bestaat een BEVESTIGDE versie met lifecycle ACTIEF en (expiresAt null of ≥ vandaag)?
 *        → binnen waarschuwingstermijn? VERLOOPT_BINNENKORT : ACTIEF
 *   1b. anders: ligt er een zelf aangeleverd CONCEPT? → WACHT_OP_CONTROLE
 *   2. anders: loopt er een deelname (AANGEMELD/INGEPLAND/BEZIG, met datum)?
 *        sessie vandaag/lopend                → IN_OPLEIDING
 *        sessie in de toekomst                → GEPLAND
 *        sessie voorbij, geen nieuwe versie   → WACHT_OP_CERTIFICAAT
 *      Een VOORLOPIGE sessie (cursusaanvraag, FO-468) telt alleen als GEPLAND:
 *      met een datum in de toekomst, een deelname die nog ingepland staat (niet
 *      vrijgegeven na een afwijzing) en een aanvraag die niet is afgewezen of
 *      ingetrokken. Nooit als lopend of voorbij - een onbevestigde cursus heeft
 *      niet plaatsgevonden - dus dan gewoon verder met regel 3/4.
 *   3. anders: ooit een versie gehad?
 *        jongste versie NIET_VAN_TOEPASSING   → NIET_VAN_TOEPASSING
 *        anders                               → VERLOPEN
 *   4. anders                                 → ONTBREEKT
 *
 * GEANNULEERD-versies (foutieve invoer) tellen nergens in mee. Idem
 * AFGEWEZEN-concepten (batch-54): een afgekeurd bewijsstuk mag de historie niet
 * maskeren - zonder die uitsluiting zou een afwijzing een geldig verleden op
 * VERLOPEN zetten. Een CONCEPT telt evenmin als certificaat, maar levert wel de
 * eigen status WACHT_OP_CONTROLE op (regel 1b) - zichtbaar "in behandeling",
 * nergens geldig.
 */
export function bepaalTypeStatus<
  V extends CertificaatVersieInput,
  D extends CertificaatDeelnameInput,
>(versies: V[], deelnames: D[], opts: BepaalTypeStatusOpts): TypeStatusResultaat<V, D> {
  const vandaagYmd = nlDagVanInstant(opts.vandaag)
  const termijnen = opts.waarschuwingstermijnenDagen.filter(
    (d) => Number.isFinite(d) && d >= 0,
  )
  const maxTermijn = termijnen.length > 0 ? Math.max(...termijnen) : 0

  const nietGeannuleerd = versies.filter((v) => v.lifecycle !== 'GEANNULEERD')
  const relevanteVersies = nietGeannuleerd.filter(isBevestigd)

  // 1. Actieve geldige versie — bij meerdere wint de langst geldige (null =
  //    verloopt nooit, wint altijd).
  const geldige = relevanteVersies.filter(
    (v) =>
      v.lifecycle === 'ACTIEF' &&
      (v.expiresAt === null || nlDagVanInstant(v.expiresAt) >= vandaagYmd),
  )
  if (geldige.length > 0) {
    const leidende = geldige.reduce((beste, v) => {
      if (beste.expiresAt === null) return beste
      if (v.expiresAt === null) return v
      return nlDagVanInstant(v.expiresAt) > nlDagVanInstant(beste.expiresAt) ? v : beste
    })
    if (leidende.expiresAt === null) {
      return { status: 'ACTIEF', leidendeVersie: leidende, relevanteDeelname: null, dagenTotVerloop: null }
    }
    const dagen = dagenTussenNlDagen(vandaagYmd, nlDagVanInstant(leidende.expiresAt))
    return {
      status: dagen <= maxTermijn ? 'VERLOOPT_BINNENKORT' : 'ACTIEF',
      leidendeVersie: leidende,
      relevanteDeelname: null,
      dagenTotVerloop: dagen,
    }
  }

  // Jongste bevestigde, niet-geannuleerde versie — context voor regel 2 en 3.
  const jongste =
    relevanteVersies.length > 0
      ? relevanteVersies.reduce((a, b) => (b.versie > a.versie ? b : a))
      : null

  // 1b. Zelf aangeleverd concept dat nog op HR wacht (batch-54, FO-408 §A4).
  //     Staat NA regel 1: is er nog een geldig bevestigd certificaat, dan wint
  //     dat (de upload is dan een vroege vervanger, geen gat). Staat VÓÓR de
  //     pipeline-regel: "ik heb het papiertje al ingeleverd" is voor de
  //     medewerker én HR het actuelere signaal dan "er staat een cursus
  //     gepland". Nooit geldig - `certGeldigheidBucket` valt via de default in
  //     ONGELDIG en de screening behandelt hem expliciet.
  const conceptVersies = nietGeannuleerd.filter(
    (v) => v.verificatieStatus === 'CONCEPT' && v.lifecycle !== 'VERVANGEN',
  )
  if (conceptVersies.length > 0) {
    const nieuwsteConcept = conceptVersies.reduce((a, b) => (b.versie > a.versie ? b : a))
    return {
      status: 'WACHT_OP_CONTROLE',
      leidendeVersie: nieuwsteConcept,
      relevanteDeelname: null,
      dagenTotVerloop: null,
    }
  }

  // 2. Pipeline-deelnames (met datum; geannuleerde sessies tellen niet; een
  //    voorlopige sessie van een afgewezen/ingetrokken aanvraag evenmin).
  const pipeline = deelnames.filter(
    (d) =>
      PIPELINE_DEELNAME_STATUSSEN.has(d.status) &&
      d.sessieStatus !== 'GEANNULEERD' &&
      d.sessieDatum !== null &&
      (!isVoorlopig(d) || voorlopigGepland(d)),
  )
  if (pipeline.length > 0) {
    const metDagen = pipeline.map((d) => ({
      deelname: d,
      // sessieDatum is non-null door het filter hierboven.
      start: nlDagVanInstant(d.sessieDatum as Date | string),
      eind: nlDagVanInstant((d.sessieEindDatum ?? d.sessieDatum) as Date | string),
    }))

    // Een voorlopige sessie is nooit "lopend" of "voorbij" (zie regel 2 hierboven).
    const bevestigd = metDagen.filter((d) => !isVoorlopig(d.deelname))
    const lopend = bevestigd.filter((d) => d.start <= vandaagYmd && d.eind >= vandaagYmd)
    if (lopend.length > 0) {
      return {
        status: 'IN_OPLEIDING',
        leidendeVersie: jongste,
        relevanteDeelname: lopend[0].deelname,
        dagenTotVerloop: null,
      }
    }

    const toekomstig = metDagen
      .filter((d) => d.start > vandaagYmd)
      .sort((a, b) => (a.start < b.start ? -1 : 1))
    if (toekomstig.length > 0) {
      return {
        status: 'GEPLAND',
        leidendeVersie: jongste,
        relevanteDeelname: toekomstig[0].deelname,
        dagenTotVerloop: null,
      }
    }

    // Alle bevestigde pipeline-sessies zijn voorbij en er is geen nieuwe versie
    // (anders had regel 1 al gewonnen): wachten op het certificaat. Alleen
    // voorlopige sessies van vandaag of eerder: niets gepland, door naar regel 3/4.
    if (bevestigd.length > 0) {
      const recentst = [...bevestigd].sort((a, b) => (a.eind > b.eind ? -1 : 1))[0]
      return {
        status: 'WACHT_OP_CERTIFICAAT',
        leidendeVersie: jongste,
        relevanteDeelname: recentst.deelname,
        dagenTotVerloop: null,
      }
    }
  }

  // 3. Ooit een versie gehad?
  if (jongste) {
    return {
      status: jongste.lifecycle === 'NIET_VAN_TOEPASSING' ? 'NIET_VAN_TOEPASSING' : 'VERLOPEN',
      leidendeVersie: jongste,
      relevanteDeelname: null,
      dagenTotVerloop: null,
    }
  }

  // 4. Nooit een versie, geen pipeline.
  return { status: 'ONTBREEKT', leidendeVersie: null, relevanteDeelname: null, dagenTotVerloop: null }
}
